import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useLazyQuery, useApolloClient } from '@apollo/client';
import {
  StaffActiveCaseListIdentityVerificationCaseV1CurrentQueryDocument,
  StaffActiveCaseListContainerIdentityVerificationCaseV1Document,
  StaffCasesIdentityVerificationV1RelatedCasesTabContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1ActiveCaseListContainerIdentityVerificationCaseV1FieldsFragment,
  StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantDocument,
  CasesIdentityVerificationRelatedCasesTabContainerArchivedIdentityVerificationCaseSummaryDocument,
  StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantQuery
} from '../../../../../../generated';
import { RelatedCasesTab } from './related-cases-tab';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';

interface RelatedCasesTabContainerProps {
  caseId: string;
  isArchived?: boolean;
}

export const RelatedCasesTabContainer: React.FC<RelatedCasesTabContainerProps> = (props) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [applicantId, setApplicantId] = useState<string | null>(null);
  const client = useApolloClient();

  const {
    data: caseData,
    loading: caseLoading,
    error: caseError
  } = useQuery(StaffCasesIdentityVerificationV1RelatedCasesTabContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId
    },
    skip: props.isArchived,
    context: {
      headers: {
        'cache-enabled': 'true'
      }
    }
  });

  const {
    loading: archivedApplicantLoading,
    data: archivedApplicantData,
    error: archivedApplicantError
  } = useQuery(CasesIdentityVerificationRelatedCasesTabContainerArchivedIdentityVerificationCaseSummaryDocument, {
    variables: {
      archivedIdentityVerificationCaseSummaryId: props.caseId
    },
    skip: !props.isArchived
  });

  const {
    loading: applicantLoading,
    data: applicantData,
    error: applicantError
  } = useQuery(StaffActiveCaseListContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId
    },
    skip: props.isArchived,
    context: {
      headers: {
        'cache-enabled': 'true'
      }
    }
  });

  const { loading } = useQuery(StaffActiveCaseListIdentityVerificationCaseV1CurrentQueryDocument, {
    variables: {
      applicantId: selectedId
    },
    skip: !selectedId
  });

  const updateIdCaseCache = async (newData: CasesIdentityVerificationV1ActiveCaseListContainerIdentityVerificationCaseV1FieldsFragment) => {
    client.cache.updateQuery(
      {
        query: StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantDocument,
        variables: { applicantId: selectedId }
      },
      (existingData) => {
        if (!existingData) {
          return {
            identityVerificationCasesV1ByApplicant: [newData]
          } as StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantQuery;
        }

        if (existingData.identityVerificationCasesV1ByApplicant?.find((item) => item?.id === newData.id)) {
          return existingData;
        }

        return {
          ...existingData,
          identityVerificationCasesV1ByApplicant: [newData, ...(existingData.identityVerificationCasesV1ByApplicant ?? [])]
        } as StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantQuery;
      }
    );
  };

  const [createRelatedIdVerificationCase, { loading: idVerificationCaseLoading, error: idVerificationCaseError }] = useLazyQuery(
    StaffActiveCaseListIdentityVerificationCaseV1CurrentQueryDocument,
    {
      variables: {
        applicantId: selectedId
      },
      onCompleted: (newData) => {
        if (!newData.identityVerificationCaseV1Current) {
          return;
        }
        updateIdCaseCache(newData.identityVerificationCaseV1Current);
      }
    }
  );
  const shouldDisable = useMemo(() => {
    if (caseData) {
      const isCurrentUserAssigned = caseData.identityVerificationCaseV1?.isCurrentUserAssigned;
      return !isCurrentUserAssigned;
    }
  }, [caseData]);

  // Effect to update applicantId based on whether the case is archived or not
  useEffect(() => {
    if (props.isArchived) {
      setApplicantId(archivedApplicantData?.archivedIdentityVerificationCaseSummary?.applicant?.id ?? null);
    } else {
      setApplicantId(applicantData?.identityVerificationCaseV1?.applicant?.id ?? null);
    }
  }, [props.isArchived, archivedApplicantData, applicantData]);

  const handleMenuClick = (e: any) => {
    if (e.key === 'id-verification') {
      setSelectedId(applicantId);
      createRelatedIdVerificationCase();
    } else {
      setSelectedId(null);
    }
  };

  return (
    <ComponentQueryLoader
      loading={caseLoading || loading || applicantLoading || idVerificationCaseLoading || archivedApplicantLoading}
      error={caseError || idVerificationCaseError || applicantError || archivedApplicantError}
      hasData={applicantData?.identityVerificationCaseV1?.applicant?.id || archivedApplicantData?.archivedIdentityVerificationCaseSummary?.applicant?.id}
      hasDataComponent={
        <RelatedCasesTab handleMenuClick={handleMenuClick} caseId={props.caseId} selectedId={selectedId} disabled={shouldDisable} applicantId={applicantId as string} />
      }
    />
  );
};
